import { buildApiUrl, buildAssetUrl } from '../../utils/api';
import { getCookie } from '../../utils/cookies';
import { getActivePincode } from '../../utils/location';

const normalizeSearchResult = (product) => ({
  ...product,
  id: product.product_id ?? product.id,
  product_id: product.product_id ?? product.id,
  name: product.name ?? product.product_name ?? 'Product',
  product_name: product.product_name ?? product.name ?? 'Product',
  image: buildAssetUrl(
    product.image ?? product.primary_image ?? product.product_image ?? product.image1 ?? null,
  ),
  price: Number.parseFloat(product.price ?? product.selling_price ?? product.starting_price ?? 0) || 0,
  mrp: Number.parseFloat(product.mrp ?? 0) || 0,
  discount_percentage: product.discount_percentage ?? null,
  variant_name: product.variant_name ?? null,
  unit: product.variants?.[0]?.unit_name ?? product.variants?.[0]?.unit ?? product.unit_name ?? (product.unit && product.unit !== 'unit' ? product.unit : ''),
  unit_name: product.variants?.[0]?.unit_name ?? product.unit_name ?? product.variant_name ?? null,
});

export const createSearchSlice = (set, get) => ({
  // ── Search query & results ─────────────────────────────────────────────────
  searchQuery: '',
  searchResults: [],
  searchLoading: false,
  searchError: null,

  // ── Search landing data (recent + trending + suggested products) ───────────
  searchLandingData: null,      // { recent_searches[], trending_searches[], suggested_products[] }
  searchLandingLoading: false,
  searchLandingError: null,

  // ── Actions ────────────────────────────────────────────────────────────────

  setSearchQuery: (query) => set({ searchQuery: query }),

  /** GET /api/v1/customer/search/landing
   *  Requires auth. Returns recent_searches, trending_searches, suggested_products.
   *  Called when the search bar is focused with an empty query.
   */
  fetchSearchLanding: async () => {
    const token = getCookie('authToken');
    if (!token) return; // guest users get no personalised landing data

    if (get().searchLandingLoading) return; // prevent duplicate in-flight calls

    set({ searchLandingLoading: true, searchLandingError: null });
    try {
      const res = await fetch(buildApiUrl('api/v1/customer/search/landing'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to fetch search landing');

      const data = json.data ?? {};
      set({
        searchLandingData: {
          recent_searches: Array.isArray(data.recent_searches) ? data.recent_searches : [],
          trending_searches: Array.isArray(data.trending_searches) ? data.trending_searches : [],
          suggested_products: Array.isArray(data.suggested_products)
            ? data.suggested_products.map(normalizeSearchResult)
            : [],
        },
        searchLandingLoading: false,
      });
    } catch (err) {
      set({ searchLandingError: err.message, searchLandingLoading: false });
    }
  },

  /** GET /api/v1/customer/products/search?q=<query>&limit=<limit>
   *  Public — no auth required.
   */
  searchProducts: async (query, limit = 10) => {
    if (!query || query.trim().length < 2) {
      set({ searchResults: [], searchLoading: false });
      return;
    }

    set({ searchLoading: true, searchError: null });
    try {
      const pin = getActivePincode(get());
      const params = new URLSearchParams({
        q: query,
        limit: String(limit),
      });
      if (pin) params.set('pincode', pin);

      const res = await fetch(
        buildApiUrl(`api/v1/customer/products/search?${params.toString()}`),
        { headers: { 'Content-Type': 'application/json' } },
      );
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Search failed');
      // Trust the API results directly — the server performs full-text matching
      // across all product fields (descriptions, tags, aliases, etc.).
      // A client-side re-filter would incorrectly drop valid results because the
      // frontend only has access to a small subset of those fields.
      const results = (json.data || []).map(normalizeSearchResult).slice(0, limit);
      set({ searchResults: results, searchLoading: false });
    } catch (err) {
      set({ searchError: err.message, searchLoading: false, searchResults: [] });
    }
  },

  /** POST /api/v1/customer/search/history  { query }
   *  Records the search term server-side. Optimistically updates local state.
   *  Silently skipped for guests (no token).
   */
  saveSearchHistory: async (query) => {
    const token = getCookie('authToken');
    if (!token || !query?.trim()) return;

    const trimmed = query.trim().toLowerCase();

    // Optimistic local update — prepend, deduplicate, cap at 10
    const prev = get().searchLandingData;
    if (prev) {
      const updated = [trimmed, ...(prev.recent_searches || []).filter((q) => q !== trimmed)].slice(0, 10);
      set({ searchLandingData: { ...prev, recent_searches: updated } });
    }

    try {
      await fetch(buildApiUrl('api/v1/customer/search/history'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: trimmed }),
      });
      // Re-sync with server (fire-and-forget)
      get().fetchSearchLanding();
    } catch {
      // Non-critical — fail silently
    }
  },

  /** DELETE /api/v1/customer/search/history
   *  Clears all search history server-side and locally.
   *  Requires auth.
   */
  clearSearchHistory: async () => {
    const token = getCookie('authToken');
    if (!token) return;

    // Optimistic local clear
    const prev = get().searchLandingData;
    if (prev) {
      set({ searchLandingData: { ...prev, recent_searches: [] } });
    }

    try {
      await fetch(buildApiUrl('api/v1/customer/search/history'), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch {
      // Non-critical — fail silently
    }
  },

  clearSearch: () => set({ searchQuery: '', searchResults: [], searchError: null }),
});
